import type { Birthday } from './valueObjects/birthday';
import type { ChildId } from './valueObjects/childId';
import type { FamilyName } from './valueObjects/familyName';
import type { FamilyNameKana } from './valueObjects/familyNameKana';
import type { Gender } from './valueObjects/gender';
import type { Name } from './valueObjects/name';
import type { NameKana } from './valueObjects/nameKana';
import type { ParentId } from './valueObjects/parentId';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';

import { Entity } from '@/domain/entity';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 子どもプロパティ
 */
export type ChildProps = {
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 名前 */
  name: Name,
  /** 名前かな */
  nameKana: NameKana,
  /** 苗字 */
  familyName: FamilyName,
  /** 苗字かな */
  familyNameKana: FamilyNameKana,
  /** 性別 */
  gender: Gender,
  /** 誕生日 */
  birthday: Birthday,
}

/**
 * 子どもEntity
 */
export class ChildEntity extends Entity<
  ChildProps,
  ChildJson
> {
  get deleteType(): DeleteType {
    return DeleteType.ParentChildId;
  }

  get deleteKey(): string {
    return this.parentChildId;
  }

  toJsonData(): ChildJson {
    return {
      parentId: this.parentId.value,
      childId: this.childId.value,
      name: this.name.value,
      nameKana: this.nameKana.value,
      familyName: this.familyName.value,
      familyNameKana: this.familyNameKana.value,
      gender: this.gender.value,
      birthday: this.birthday.value.toJSON(),
    };
  }

  /** ラベル名 */
  static get labelName() {
    return '氏名';
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    return EntityName.WagakoAppChildChildEntity;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return this.childId.value;
  }

  /**
   * Entityキー子ID
   */
  get entityKeyChildId(): string {
    return this.childId.value;
  }

  /** 親子ID */
  get parentChildId() {
    return `${this.parentId.value}_${this.childId.value}`;
  }

  /** 親ID */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /** 子ID */
  get childId(): ChildId {
    return this._value.childId;
  }

  /** 名前 */
  get name(): Name {
    return this._value.name;
  }

  /** 名前 */
  set name(name: Name) {
    this._value.name = name;
  }

  /** 名前かな */
  get nameKana(): NameKana {
    return this._value.nameKana;
  }

  /** 名前かな */
  set nameKana(nameKana: NameKana) {
    this._value.nameKana = nameKana;
  }

  /** 苗字 */
  get familyName(): FamilyName {
    return this._value.familyName;
  }

  /** 苗字 */
  set familyName(familyName: FamilyName) {
    this._value.familyName = familyName;
  }

  /** 苗字かな */
  get familyNameKana(): FamilyNameKana {
    return this._value.familyNameKana;
  }

  /** 苗字かな */
  set familyNameKana(familyNameKana: FamilyNameKana) {
    this._value.familyNameKana = familyNameKana;
  }

  /** 性別 */
  get gender(): Gender {
    return this._value.gender;
  }

  /** 性別 */
  set gender(gender: Gender) {
    this._value.gender = gender;
  }

  /** 誕生日 */
  get birthday(): Birthday {
    return this._value.birthday;
  }

  /** 誕生日 */
  set birthday(birthday: Birthday) {
    this._value.birthday = birthday;
  }
}
